import { T, formatMoney, tt } from '@agos/shared'
import { cn } from '../cn'
import { BUTTON_DANGER, BUTTON_GHOST, BUTTON_SECONDARY } from './button-classes'
import { AlertTriangle, Ban, Eye, EyeOff, Loader2, X } from 'lucide-react'
import { useState, type FormEvent } from 'react'

/**
 * Cancelling an order needs a reason, and sometimes somebody's credentials -
 * ported from legacy `components/cancel-order-modal.tsx` and then narrowed by
 * §9.341.
 *
 * It is not a confirmation dialog with extra fields: the API's
 * `/orders/{id}/confirm-status` records who cancelled and why. A cancellation
 * moves money and reverses stock, so it is attributable by design.
 *
 * **`askCredentials` is what §9.341 added, and the owner is why.** *"These
 * popups are useless."* The port asked for an e-mail and a password
 * unconditionally, so a `main_admin` was asked to retype the password they had
 * signed in with in order to authorise something their session already
 * authorises - `OrderActorResolver.resolve` falls back to the signed-in
 * principal when both are absent.
 *
 * **They are still asked when the session cannot authorise it**, which is the
 * half worth keeping: `cancelled` needs `{main_admin, admin}`, so on a till
 * signed in as a waiter this box is the *manager walks over* flow. The caller
 * decides with `credentialsNeeded(role, status)`; the reason is always asked,
 * because `reason` is `@NotBlank` server-side and a default would put a fiction
 * in an audit trail.
 */

interface CancellableOrder {
  id?: number
  type?: string
  total?: number | string
  tableName?: string | null
  tableNumber?: number | string | null
  customerName?: string | null
  orderNumber?: string | number | null
}

const FIELD_CLASSES =
  'w-full px-3 py-2.5 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

export function CancelOrderModal({
  order,
  onClose,
  onConfirm,
  subtitle,
  title,
  confirmLabel,
  askCredentials = true,
}: {
  order: CancellableOrder
  onClose: () => void
  onConfirm: (email: string, password: string, reason: string) => Promise<void>
  /** Overrides the line under the title; otherwise derived from the order. */
  subtitle?: string
  /**
   * The heading, and the label on the button that commits - §9.409.
   *
   * **Both default to the cancel wording, so a caller that says nothing keeps what it had.**
   * §9.341 reused this dialog for all three settling statuses and made only the *subtitle* name
   * which one, leaving a close headed *Stornovať objednávku* with a *Stornovať* button - the exact
   * failure `board.ts` predicted in writing. The owner found it by pressing *Vydaná*.
   *
   * `RestaurantPage` and `PreordersPage` pass neither, deliberately: they open this for a real
   * `cancelled` and the defaults are right for them.
   */
  title?: string
  confirmLabel?: string
  /**
   * Whether to ask for an administrator's credentials as well as the reason.
   *
   * Defaults to `true` so every existing caller keeps the behaviour it had -
   * §9.341 is additive, and a screen that has not been told the signed-in role
   * must not quietly stop asking.
   */
  askCredentials?: boolean
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [reason, setReason] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    // The reason is always required - it is `@NotBlank` on the server and it is the one thing only
    // the person cancelling knows. The credentials are required only when asked for.
    if (!reason.trim() || (askCredentials && (!email.trim() || !password.trim()))) {
      setError(askCredentials ? tt('Vyplňte všetky polia') : tt('Uveďte dôvod storna'))
      return
    }

    setIsSubmitting(true)
    setError('')
    try {
      await onConfirm(email, password, reason)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : tt('Neplatné prihlasovacie údaje'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const derivedSubtitle =
    subtitle ??
    (order.type === 'bar'
      ? `${tt('Osobný odber')} - ${formatMoney(order.total)}`
      : (order.tableName ?? order.tableNumber)
        ? `${tt('Stôl')} ${order.tableName ?? order.tableNumber} - ${formatMoney(order.total)}`
        : `${order.customerName ?? `#${order.orderNumber ?? order.id}`} - ${formatMoney(order.total)}`)

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-start gap-3 px-6 py-5 border-b border-border">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-foreground">
              {title === undefined ? <T>Stornovať objednávku</T> : <T>{title}</T>}
            </h2>
            <p className="text-sm text-muted-foreground truncate">{derivedSubtitle}</p>
          </div>
          <button
            type="button"
            aria-label={tt('Zatvoriť')}
            onClick={onClose}
            className={cn(BUTTON_GHOST, 'shrink-0')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={(event) => void submit(event)} className="p-6 space-y-4">
          <p className="text-xs text-muted-foreground" data-cancel-intro>
            {askCredentials
              ? /* §9.341 - said only when it is true. This is the *manager walks over* case: the
                 * signed-in account may not storno, so somebody who may has to authorise it. */
                tt('Toto konto nemôže stornovať. Zadajte údaje administrátora a dôvod - storno sa zaznamená na jeho meno.')
              : tt('Storno sa zaznamená na vaše meno. Uveďte dôvod.')}
          </p>

          {askCredentials && (
            <>
          <div>
            <label className="text-sm font-semibold block mb-1.5" htmlFor="cancel-order-email">Email</label>
            <input
              id="cancel-order-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className={FIELD_CLASSES}
              autoComplete="username"
            />
          </div>

          <div>
            <label className="text-sm font-semibold block mb-1.5" htmlFor="cancel-order-heslo">
              <T>Heslo</T>
            </label>
            <div className="relative">
              <input
                id="cancel-order-heslo"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className={cn(FIELD_CLASSES, 'pr-10')}
                autoComplete="current-password"
              />
              <button
                type="button"
                aria-label={tt('Zobraziť heslo')}
                onClick={() => setShowPassword((shown) => !shown)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
            </>
          )}

          <div>
            <label className="text-sm font-semibold block mb-1.5" htmlFor="cancel-order-dovod-storna">
              <T>Dôvod storna</T>
            </label>
            <input
              id="cancel-order-dovod-storna"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={tt('napr. zákazník zrušil telefonicky')}
              className={FIELD_CLASSES}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
            >
              <T>Späť</T>
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={cn('flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm', BUTTON_DANGER)}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Ban className="w-4 h-4" />
              )}
              {confirmLabel === undefined ? <T>Stornovať</T> : <T>{confirmLabel}</T>}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
